import React, { useState } from "react";
import Field from "./Field";
import PhoneField from "./PhoneField";
import { COUNTRIES, byName } from "./countries";

/** Step 2: the property's name, contact details and location, on one page in three short groups. */
export default function DetailsStep({ data, patch, errors, onBlur }) {
  const [otherCountry, setOtherCountry] = useState(!!data.Country && !byName(data.Country));
  const type = data.Type || "Property";
  const text = (name) => ({
    id: `f-${name}`,
    value: data[name] || "",
    onChange: (e) => patch({ [name]: e.target.value }),
    onBlur: () => onBlur(name),
    className: "auth-input",
    "aria-invalid": errors[name] ? true : undefined,
    "aria-describedby": `f-${name}-msg`,
  });

  const pickCountry = (value) => {
    if (value === "other") {
      setOtherCountry(true);
      patch({ Country: "" });
    } else {
      setOtherCountry(false);
      const c = byName(value);
      patch({ Country: value, CountryDialCode: c ? c.dial : data.CountryDialCode });
    }
  };

  return (
    <div>
      <h2 className="reg-title">Tell us about your {type.toLowerCase()}</h2>
      <p className="reg-sub">Guests and invoices will show these details.</p>

      <fieldset className="reg-group">
        <legend>About</legend>
        <Field id="f-HotelName" label={`${type} name`} error={errors.HotelName}>
          <input {...text("HotelName")} autoComplete="organization" placeholder={`e.g. Savanna ${type}`} />
        </Field>
      </fieldset>

      <fieldset className="reg-group">
        <legend>Contact</legend>
        <div className="reg-row">
          <Field id="f-Email" label="Business email" error={errors.Email} hint="Where guests and the system reach the property">
            <input {...text("Email")} type="email" inputMode="email" autoComplete="email" />
          </Field>
          <Field id="f-ContactNumber" label="Phone" error={errors.ContactNumber}>
            <PhoneField
              id="f-ContactNumber"
              dial={data.CountryDialCode}
              number={data.ContactNumber || ""}
              onDial={(d) => patch({ CountryDialCode: d })}
              onNumber={(n) => patch({ ContactNumber: n })}
              onBlur={() => onBlur("ContactNumber")}
              invalid={!!errors.ContactNumber}
              describedBy="f-ContactNumber-msg"
            />
          </Field>
        </div>
        <div className="reg-row">
          <Field id="f-Address" label="Postal address" error={errors.Address}>
            <input {...text("Address")} autoComplete="street-address" placeholder="P.O. Box 123" />
          </Field>
          <Field id="f-Website" label="Website" optional error={errors.Website}>
            <input {...text("Website")} type="text" inputMode="url" autoComplete="url" placeholder="www.example.com" />
          </Field>
        </div>
      </fieldset>

      <fieldset className="reg-group">
        <legend>Location</legend>
        <div className="reg-row">
          <Field id="f-Country" label="Country" error={errors.Country}>
            <select
              id="f-Country"
              className="auth-input"
              value={otherCountry ? "other" : data.Country || ""}
              onChange={(e) => pickCountry(e.target.value)}
              onBlur={() => onBlur("Country")}
              aria-invalid={errors.Country ? true : undefined}
              aria-describedby="f-Country-msg"
            >
              <option value="" disabled>
                Select a country
              </option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.name}>
                  {c.name}
                </option>
              ))}
              <option value="other">Other (type it)</option>
            </select>
            {otherCountry && (
              <input
                className="auth-input"
                style={{ marginTop: 8 }}
                aria-label="Country name"
                placeholder="Type your country"
                value={data.Country || ""}
                onChange={(e) => patch({ Country: e.target.value })}
                onBlur={() => onBlur("Country")}
              />
            )}
          </Field>
          <Field id="f-Province" label="Province / region" error={errors.Province}>
            <input {...text("Province")} autoComplete="address-level1" />
          </Field>
        </div>
        <div className="reg-row">
          <Field id="f-District" label="District" error={errors.District}>
            <input {...text("District")} />
          </Field>
          <Field id="f-TownOrVillage" label="Town / village" error={errors.TownOrVillage}>
            <input {...text("TownOrVillage")} autoComplete="address-level2" />
          </Field>
        </div>
        <Field id="f-PhysicalLocation" label="Plot number / physical location" error={errors.PhysicalLocation}>
          <input {...text("PhysicalLocation")} placeholder="e.g. Plot 1234, Main Mall" />
        </Field>
      </fieldset>
    </div>
  );
}
